// ────────────────────────────────────────────────────────────────────────────
// "What would this do?" — the rules as currently configured, run against sample
// orders, showing exactly what an operator would see.
//
// This is the same idea as the live PDF preview in the Documents builder, and it
// exists for the same reason: a configuration screen that cannot show its own
// effect makes people guess. Three orders on purpose — one clean, one blocked,
// one allowed-with-a-warning — because the warning case is the one a design
// usually gets wrong.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { AlertTriangle, Ban, Check } from 'lucide-react';
import { evaluate, isBlocked, samplesFor, type Sample } from '../lib/evaluate';
import { requirementMeta, stateLabel, type Workflow, type WorkflowTransition } from '../lib/mockWorkflow';

export function TransitionPreview({
  workflow,
  transition,
  referenceLabels,
}: {
  workflow: Workflow;
  transition: WorkflowTransition;
  /** Only for wording — "Cust Ref is empty" rather than "1 is empty". */
  referenceLabels: string[];
}) {
  const from = stateLabel(workflow, transition.from);
  const to = stateLabel(workflow, transition.to);
  const samples = samplesFor(workflow.subject);

  return (
    <div className="flex h-full flex-col rounded-lg border border-slate-200 bg-white">
      <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          What happens on real records
        </h3>
        <span className="text-[11px] text-slate-400">
          {from} to {to}
        </span>
      </div>

      {samples.length === 0 ? (
        <p className="flex-1 px-4 py-6 text-center text-xs text-slate-400">
          No sample data for this track yet — these conditions cannot be tried out until the
          underlying records exist.
        </p>
      ) : (
        <ul className="flex-1 divide-y divide-slate-100">
          {samples.map((sample) => (
            <SampleRow
              key={sample.id}
              sample={sample}
              transition={transition}
              to={to}
              referenceLabels={referenceLabels}
            />
          ))}
        </ul>
      )}

      <p className="border-t border-slate-100 bg-slate-50 px-4 py-1.5 text-[10px] text-slate-400">
        Samples. Change a condition above and these update — this is the message the operator
        would get.
      </p>
    </div>
  );
}

function SampleRow({
  sample,
  transition,
  to,
  referenceLabels,
}: {
  sample: Sample;
  transition: WorkflowTransition;
  to: string;
  referenceLabels: string[];
}) {
  const unmet = evaluate(sample, transition, referenceLabels);
  const blocked = isBlocked(unmet);
  const warnings = unmet.filter((u) => u.severity === 'warn');

  return (
    <li className="flex flex-wrap items-start gap-3 px-4 py-2.5">
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
          blocked
            ? 'bg-red-100 text-red-700'
            : warnings.length > 0
              ? 'bg-amber-100 text-amber-800'
              : 'bg-emerald-100 text-emerald-700',
        )}
      >
        {blocked ? (
          <Ban className="h-3 w-3" />
        ) : warnings.length > 0 ? (
          <AlertTriangle className="h-3 w-3" />
        ) : (
          <Check className="h-3 w-3" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="font-mono text-xs text-slate-800">{sample.ref}</p>
          <p className="text-[11px] text-slate-400">{sample.party}</p>
          <p className="text-[11px] text-slate-400">{summarise(sample)}</p>
        </div>

        {/* The operator-facing sentence. Blocked says what to fix; a warning
            says what is odd but lets the work continue. */}
        <p
          className={cn(
            'mt-1 text-xs',
            blocked ? 'text-red-700' : warnings.length > 0 ? 'text-amber-800' : 'text-emerald-700',
          )}
        >
          {blocked
            ? `Cannot move to ${to} — ${unmet
                .filter((u) => u.severity === 'stop')
                .map((u) => u.reason.toLowerCase())
                .join('; ')}`
            : warnings.length > 0
              ? `Moves to ${to} with a warning — ${warnings
                  .map((u) => u.reason.toLowerCase())
                  .join('; ')}`
              : `Moves to ${to} cleanly`}
        </p>

        {unmet.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {unmet.map((u) => (
              <li key={u.kind} className="flex items-center gap-1.5 text-[11px]">
                <span
                  className={cn(
                    'rounded px-1 font-medium',
                    u.severity === 'stop'
                      ? 'bg-red-50 text-red-700'
                      : 'bg-amber-50 text-amber-800',
                  )}
                >
                  {u.severity === 'stop' ? 'Block' : 'Warn'}
                </span>
                <span className="text-slate-500">{requirementMeta(u.kind).label}</span>
                <span className="text-slate-400">— {u.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/** One line of context per sample, so a blocked row explains itself. */
function summarise(sample: Sample): string {
  if (sample.kind === 'order') {
    const t = sample.tasks.length;
    const c = sample.chargeLines.length;
    return `${t} task${t === 1 ? '' : 's'} - ${c} charge${c === 1 ? '' : 's'}`;
  }
  const done = sample.legs.filter((l) => l.state === 'completed').length;
  return `${sample.lines} lines - ${done}/${sample.legs.length} legs done - ${
    sample.driver ?? 'no driver'
  }`;
}
